import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, QrCode, ExternalLink, Percent, Lock, Zap, Copy, Save } from "lucide-react";
import { api, formatError, resolveFileUrl } from "../../lib/api";
import { AdminHeader, AdminModal, AField } from "../../components/AdminLayout";
import ImageDropzone from "../../components/ImageDropzone";
import { useAuth } from "../../context/AuthContext";

const MASTER_EMAIL = "darkchatasg@gmail.com";
const BLANK = { label: "", kind: "paynow", qr_image: "", url: "", active: true, order: 0 };

function HitPayConfig({ isMaster }) {
  const [cfg, setCfg] = useState(null);
  const [mode, setMode] = useState("live");
  const [enabled, setEnabled] = useState(true);
  const [keys, setKeys] = useState({ live: { api_key: "", salt: "" }, sandbox: { api_key: "", salt: "" } });
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/admin/hitpay-config").then((r) => { setCfg(r.data); setMode(r.data.mode); setEnabled(r.data.enabled); }).catch(() => {});
  useEffect(() => { load(); }, []);

  const saveMode = async (m) => {
    const k = keys[m];
    if (!k.api_key.trim() && !k.salt.trim()) { toast.error("Paste an API key or salt to save"); return; }
    setBusy(true);
    try {
      const body = { key_mode: m };
      if (k.api_key.trim()) body.api_key = k.api_key.trim();
      if (k.salt.trim()) body.webhook_salt = k.salt.trim();
      const r = await api.put("/admin/hitpay-config", body);
      setCfg(r.data); setKeys((s) => ({ ...s, [m]: { api_key: "", salt: "" } }));
      toast.success(`${m === "live" ? "Live" : "Sandbox"} keys saved`);
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const deleteMode = async (m) => {
    if (!window.confirm(`Delete the saved ${m} API key and webhook salt?`)) return;
    setBusy(true);
    try { const r = await api.put("/admin/hitpay-config", { delete: m }); setCfg(r.data); toast.success(`${m === "live" ? "Live" : "Sandbox"} keys deleted`); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const saveActive = async () => {
    setBusy(true);
    try { const r = await api.put("/admin/hitpay-config", { active_mode: mode, enabled }); setCfg(r.data); toast.success("Payment mode saved"); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  if (!cfg) return null;

  const renderSection = (m) => {
    const info = cfg[m] || {};
    const label = m === "live" ? "Live" : "Sandbox";
    return (
      <div className="rounded-xl bg-white/5 border border-white/10 p-3 space-y-2" data-testid={`hitpay-section-${m}`}>
        <div className="flex items-center justify-between">
          <p className="text-sm font-semibold text-[#E8E4F0]">{label} keys {info.has_key ? <span className="text-[11px] text-[#4ADE80] ml-1">✓ saved</span> : <span className="text-[11px] text-[#A19DB0] ml-1">not set</span>}</p>
          {isMaster && (info.has_key || info.has_salt) && (
            <button type="button" onClick={() => deleteMode(m)} disabled={busy} data-testid={`hitpay-delete-${m}`} className="text-[#E57373] text-xs flex items-center gap-1 disabled:opacity-50"><Trash2 size={12} /> Delete</button>
          )}
        </div>
        <AField label={`API key ${info.has_key ? `(saved: ${info.api_key_masked})` : "(not set)"}`} type="password" placeholder="Paste to update" value={keys[m].api_key} disabled={!isMaster} onChange={(e) => setKeys((s) => ({ ...s, [m]: { ...s[m], api_key: e.target.value } }))} data-testid={`hitpay-api-key-${m}`} />
        <AField label={`Webhook salt ${info.has_salt ? "(saved)" : "(not set)"}`} type="password" placeholder="Paste to update" value={keys[m].salt} disabled={!isMaster} onChange={(e) => setKeys((s) => ({ ...s, [m]: { ...s[m], salt: e.target.value } }))} data-testid={`hitpay-salt-${m}`} />
        {isMaster && <button type="button" onClick={() => saveMode(m)} disabled={busy} data-testid={`hitpay-save-${m}`} className="rounded-full bg-white/10 text-[#E8E4F0] font-semibold px-4 py-2 text-xs disabled:opacity-60 flex items-center gap-2"><Save size={13} /> Save {label} keys</button>}
      </div>
    );
  };

  return (
    <div className="rounded-2xl border border-white/10 bg-white/5 p-5 mb-6" data-testid="hitpay-config">
      <div className="flex items-center gap-2 mb-1">
        <Zap size={16} className="text-[#D4AF37]" /><h3 className="font-display text-lg font-semibold">HitPay online payments</h3>
        {!isMaster && <span className="flex items-center gap-1 text-[10px] uppercase text-[#A19DB0] ml-2"><Lock size={11} /> master admin only</span>}
        <span className={`ml-auto text-[11px] font-semibold rounded-full px-2.5 py-1 ${cfg.active ? "bg-[#4ADE80]/15 text-[#4ADE80]" : "bg-white/10 text-[#A19DB0]"}`}>{cfg.active ? "Active" : "Off"}</span>
      </div>
      <p className="text-[#A19DB0] text-sm mb-3">Card &amp; PayNow with automatic confirmation. Store your Live and Sandbox keys separately, then choose which is active.</p>
      <div className="space-y-3">
        <div>
          <p className="text-xs text-[#A19DB0] mb-1">Active mode</p>
          <div className="flex gap-2">
            {["live", "sandbox"].map((mm) => (
              <button key={mm} type="button" disabled={!isMaster} onClick={() => setMode(mm)} data-testid={`hitpay-mode-${mm}`} className={`flex-1 rounded-full py-2 text-sm font-medium border transition-colors ${mode === mm ? "bg-[#D4AF37] text-black border-[#D4AF37]" : "bg-white/5 border-white/10 text-[#E8E4F0]"} disabled:opacity-50`}>{mm === "live" ? "Live" : "Sandbox"}</button>
            ))}
          </div>
          {mode === "sandbox" && <p className="text-[11px] text-[#E5A84B] mt-1.5" data-testid="hitpay-sandbox-note">Sandbox is active — only admins &amp; super admins can pay by card (for testing). Regular members see a "Coming soon" message.</p>}
        </div>
        <label className="flex items-center gap-2 text-sm text-[#E8E4F0]"><input type="checkbox" checked={enabled} disabled={!isMaster} onChange={(e) => setEnabled(e.target.checked)} data-testid="hitpay-enabled" /> Show "Pay online" to members (Live mode)</label>
        {isMaster && <button onClick={saveActive} disabled={busy} data-testid="hitpay-save" className="rounded-full bg-[#D4AF37] text-black font-semibold px-5 py-2.5 text-sm disabled:opacity-60 flex items-center gap-2"><Save size={15} /> {busy ? "Saving…" : "Save mode & visibility"}</button>}
        <div className="rounded-xl bg-white/5 border border-white/10 p-3">
          <p className="text-[10px] uppercase tracking-wide text-[#A19DB0] mb-1">Register this webhook URL in HitPay (event: payment_request.completed)</p>
          <div className="flex items-center gap-2">
            <code className="text-xs text-[#D4AF37] break-all flex-1">{cfg.webhook_url}</code>
            <button type="button" onClick={() => { navigator.clipboard?.writeText(cfg.webhook_url); toast.success("Copied"); }} data-testid="hitpay-copy-webhook" className="shrink-0 bg-white/10 rounded-full px-3 py-1.5 text-xs flex items-center gap-1"><Copy size={12} /> Copy</button>
          </div>
        </div>
        {renderSection("live")}
        {renderSection("sandbox")}
      </div>
    </div>
  );
}

export default function AdminPaymentMethods() {
  const { user } = useAuth();
  const isMaster = (user?.email || "").toLowerCase() === MASTER_EMAIL;
  const [items, setItems] = useState([]);
  const [modal, setModal] = useState(null);
  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState([]);
  const [settings, setSettings] = useState({ fee_percent: 0, tax_percent: 0 });
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);

  const load = useCallback(() => api.get("/admin/payment-methods").then((r) => setItems(r.data)).catch(() => {}), []);
  useEffect(() => {
    load();
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {}).finally(() => setSettingsLoaded(true));
  }, [load]);

  const save = async () => {
    if (!modal.label.trim()) { toast.error("Label is required"); return; }
    setBusy(true);
    const payload = { label: modal.label.trim(), kind: modal.kind, qr_image: modal.qr_image || "", url: modal.url || "", active: !!modal.active, order: parseInt(modal.order, 10) || 0 };
    try {
      if (modal.id) await api.put(`/admin/payment-methods/${modal.id}`, payload);
      else await api.post("/admin/payment-methods", payload);
      toast.success("Payment method saved"); setModal(null); load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const toggleSel = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const bulkDelete = async () => {
    if (selected.length === 0) return;
    if (!window.confirm(`Delete ${selected.length} payment method(s)?`)) return;
    try { await api.post("/admin/payment-methods/bulk-delete", { ids: selected }); toast.success("Deleted"); setSelected([]); load(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      const r = await api.put("/admin/settings", { fee_percent: parseFloat(settings.fee_percent) || 0, tax_percent: parseFloat(settings.tax_percent) || 0 });
      setSettings(r.data); toast.success("Charges updated");
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setSavingSettings(false); }
  };

  return (
    <div>
      <AdminHeader title="Payment methods" subtitle="QR-code payments (PayNow / PayLah). Members scan, pay, then upload proof for approval."
        action={<button onClick={() => setModal({ ...BLANK })} data-testid="admin-add-method" className="flex items-center gap-2 bg-[#D4AF37] text-black font-semibold rounded-full px-4 py-2.5 text-sm active:scale-95 transition-transform"><Plus size={16} /> New method</button>} />

      {/* Charges (Fee & Tax) */}
      <div className="rounded-2xl border border-white/10 bg-white/5 p-5 mb-6" data-testid="charges-section">
        <div className="flex items-center gap-2 mb-1"><Percent size={16} className="text-[#D4AF37]" /><h3 className="font-display text-lg font-semibold">Charges</h3>{!isMaster && <span className="flex items-center gap-1 text-[10px] uppercase text-[#A19DB0] ml-2"><Lock size={11} /> master admin only</span>}</div>
        <p className="text-[#A19DB0] text-sm mb-3">Fee and tax are added on top of the subtotal at checkout.</p>
        <div className="flex flex-wrap items-end gap-4">
          <div className="w-32"><AField label="Fee %" type="number" min="0" step="0.01" value={settings.fee_percent} disabled={!isMaster || !settingsLoaded} onChange={(e) => setSettings({ ...settings, fee_percent: e.target.value })} data-testid="fee-input" /></div>
          <div className="w-32"><AField label="Tax %" type="number" min="0" step="0.01" value={settings.tax_percent} disabled={!isMaster || !settingsLoaded} onChange={(e) => setSettings({ ...settings, tax_percent: e.target.value })} data-testid="tax-input" /></div>
          {isMaster && <button onClick={saveSettings} disabled={savingSettings || !settingsLoaded} data-testid="save-charges" className="rounded-full bg-[#D4AF37] text-black font-semibold px-5 py-2.5 text-sm disabled:opacity-60">{savingSettings ? "Saving…" : "Save charges"}</button>}
        </div>
      </div>

      <HitPayConfig isMaster={isMaster} />

      {selected.length > 0 && (
        <div className="flex items-center justify-between rounded-xl bg-white/5 border border-white/10 px-4 py-2.5 mb-4">
          <span className="text-sm text-[#A19DB0]">{selected.length} selected</span>
          <button onClick={bulkDelete} data-testid="bulk-delete-methods" className="flex items-center gap-1.5 text-[#E57373] text-sm"><Trash2 size={14} /> Delete selected</button>
        </div>
      )}

      {items.length === 0 ? (
        <p className="text-[#A19DB0] text-sm">No payment methods yet. Add one so members can pay.</p>
      ) : (
        <div className="grid sm:grid-cols-2 gap-4">
          {items.map((m) => (
            <div key={m.id} data-testid={`method-card-${m.id}`} className={`rounded-2xl border p-5 ${m.active ? "border-white/10 bg-white/5" : "border-white/5 bg-white/[0.02] opacity-60"}`}>
              <div className="flex items-start justify-between">
                <label className="flex items-center gap-3">
                  <input type="checkbox" checked={selected.includes(m.id)} onChange={() => toggleSel(m.id)} data-testid={`select-method-${m.id}`} />
                  <div className="w-9 h-9 rounded-lg bg-[#9D4EDD]/20 flex items-center justify-center"><QrCode size={16} className="text-[#D4AF37]" /></div>
                  <div>
                    <h3 className="font-display text-xl font-semibold">{m.label}</h3>
                    <p className="text-[#A19DB0] text-xs uppercase">{m.kind}</p>
                  </div>
                </label>
                {!m.active && <span className="text-[10px] uppercase text-[#E57373]">inactive</span>}
              </div>
              {m.qr_image && <img src={resolveFileUrl(m.qr_image)} alt="QR" className="w-24 h-24 object-contain mt-3 rounded-lg bg-white p-1" />}
              {m.url && <a href={m.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-[#D4AF37] text-xs mt-2 underline"><ExternalLink size={12} /> Payment link</a>}
              <div className="flex gap-2 mt-4">
                <button onClick={() => setModal({ ...m, qr_image: m.qr_image || "", url: m.url || "" })} data-testid={`edit-method-${m.id}`} className="flex items-center gap-1.5 bg-white/5 rounded-full px-3 py-1.5 text-sm hover:bg-white/10"><Pencil size={14} /> Edit</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {modal && (
        <AdminModal open onOpenChange={() => setModal(null)} title={modal.id ? "Edit payment method" : "New payment method"} onSubmit={save} busy={busy}>
          <AField label="Label (shown to members)" value={modal.label} onChange={(e) => setModal({ ...modal, label: e.target.value })} data-testid="method-label" placeholder="e.g. Darkchata PayNow" />
          <div>
            <label className="text-xs text-[#A19DB0]">Type</label>
            <select value={modal.kind} onChange={(e) => setModal({ ...modal, kind: e.target.value })} data-testid="method-kind" className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2 mt-1 text-sm outline-none focus:border-[#D4AF37]">
              <option value="paynow" className="bg-[#140F1E]">PayNow</option>
              <option value="paylah" className="bg-[#140F1E]">PayLah</option>
            </select>
          </div>
          <ImageDropzone value={modal.qr_image} onUploaded={(url) => setModal({ ...modal, qr_image: url })} endpoint="/admin/upload-image" resultKey="url" label="QR code image (upload)" />
          <AField label="Clickable payment URL (optional)" value={modal.url} onChange={(e) => setModal({ ...modal, url: e.target.value })} data-testid="method-url-input" placeholder="https://…" />
          <AField label="Order" type="number" value={modal.order} onChange={(e) => setModal({ ...modal, order: e.target.value })} data-testid="method-order" />
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!modal.active} onChange={(e) => setModal({ ...modal, active: e.target.checked })} data-testid="method-active" /> Active</label>
        </AdminModal>
      )}
    </div>
  );
}
